import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  FiDownload,
  FiEye,
  FiFile,
  FiLoader,
  FiMail,
  FiAlertTriangle,
  FiTrash2,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";

import api from "../services/api";

import {
  deleteContainerDocument,
  getContainerDocuments,
  uploadContainerDocuments,
} from "../services/containerApi";

const DOCUMENT_TYPES = [
  "Bill of Lading",
  "Delivery Booking",
  "Customs",
  "Packing List",
  "Invoice",
  "Proof of Delivery",
  "Other",
];

const ACCEPTED_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "jpg",
  "jpeg",
  "png",
  "webp",
  "txt",
]);

const MAX_FILES = 10;
const MAX_FILE_SIZE = 20 * 1024 * 1024;

const formatBytes = (bytes = 0) => {
  const size = Number(bytes || 0);

  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

const getFileExtension = (fileName = "") => {
  const parts = String(fileName).toLowerCase().split(".");

  return parts.length > 1 ? parts.pop() : "";
};

const createFileKey = (file) =>
  `${file.name}-${file.size}-${file.lastModified}`;

const getCurrentUserId = () => {
  const possibleKeys = [
    "user",
    "authUser",
    "currentUser",
    "userData",
    "admin",
  ];

  for (const key of possibleKeys) {
    try {
      const value = localStorage.getItem(key);
      const user = value ? JSON.parse(value) : null;

      if (user?.id !== undefined && user?.id !== null) {
        return String(user.id);
      }
    } catch {
      // Ignore invalid localStorage values.
    }
  }

  return "";
};

const getErrorMessage = (error, fallback) => {
  const status = error.response?.status;
  const responseMessage = error.response?.data?.message;

  if (responseMessage) {
    return responseMessage;
  }

  if (status === 404) {
    return (
      "The container documents API route is not available " +
      "on the backend."
    );
  }

  return fallback;
};

const getDocumentFileUrl = (containerId, documentId) =>
  `/containers/${containerId}/documents/${documentId}/download`;

function RemoveDocumentModal({
  document,
  deleting = false,
  onCancel,
  onConfirm,
}) {
  useEffect(() => {
    if (!document) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !deleting) {
        onCancel();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [document, deleting, onCancel]);

  if (!document) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[11000] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-document-title"
      onMouseDown={(event) => {
        if (
          event.target === event.currentTarget &&
          !deleting
        ) {
          onCancel();
        }
      }}
    >
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="p-6">
          <div className="grid h-14 w-14 place-items-center rounded-2xl bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-300">
            <FiAlertTriangle className="text-2xl" />
          </div>

          <h3
            id="remove-document-title"
            className="mt-5 text-xl font-bold text-slate-950 dark:text-white"
          >
            Remove document?
          </h3>

          <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
            This document will be permanently removed from the container.
          </p>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                <FiFile />
              </div>

              <div className="min-w-0">
                <p
                  className="truncate text-sm font-semibold text-slate-900 dark:text-white"
                  title={document.originalName}
                >
                  {document.originalName}
                </p>

                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {document.documentType}
                  {" • "}
                  {formatBytes(document.sizeBytes)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-950/60 sm:flex-row sm:justify-end">
          <button
            type="button"
            disabled={deleting}
            onClick={onCancel}
            className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={deleting}
            onClick={onConfirm}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {deleting ? (
              <FiLoader className="animate-spin" />
            ) : (
              <FiTrash2 />
            )}

            {deleting ? "Removing..." : "Remove Document"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ContainerDocumentsPanel({
  containerId,
  canRead = false,
  canUpload = false,
  canDelete = false,
  onMessage,
}) {
  const fileInputRef = useRef(null);
  const onMessageRef = useRef(onMessage);

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [openingDocumentId, setOpeningDocumentId] = useState(null);
  const [downloadingDocumentId, setDownloadingDocumentId] =
    useState(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);
  const [documentToRemove, setDocumentToRemove] = useState(null);

  const [files, setFiles] = useState([]);
  const [documentType, setDocumentType] =
    useState("Bill of Lading");
  const [notes, setNotes] = useState("");

  const currentUserId = getCurrentUserId();

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const notify = useCallback((message, type) => {
    onMessageRef.current?.(message, type);
  }, []);

  const selectedText = useMemo(() => {
    return files.length ? "Add more documents" : "Select documents";
  }, [files.length]);

  const uploadButtonText = useMemo(() => {
    if (uploading) {
      return "Uploading...";
    }

    if (files.length === 1) {
      return "Upload 1 Document";
    }

    if (files.length > 1) {
      return `Upload ${files.length} Documents`;
    }

    return "Upload Documents";
  }, [files.length, uploading]);

  const selectedTotalSize = useMemo(
    () =>
      files.reduce(
        (total, file) => total + Number(file.size || 0),
        0
      ),
    [files]
  );

  const resetSelectedFiles = useCallback(() => {
    setFiles([]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  useEffect(() => {
    resetSelectedFiles();
    setNotes("");
  }, [containerId, resetSelectedFiles]);

  const load = useCallback(async () => {
    if (!canRead || !containerId) {
      setDocuments([]);
      return;
    }

    setLoading(true);

    try {
      const response = await getContainerDocuments(containerId);

      setDocuments(
        Array.isArray(response.data?.documents)
          ? response.data.documents
          : []
      );
    } catch (error) {
      setDocuments([]);

      notify(
        getErrorMessage(error, "Unable to load documents"),
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [canRead, containerId, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFileSelection = (event) => {
    const incomingFiles = Array.from(event.target.files || []);

    if (!incomingFiles.length) {
      return;
    }

    const invalidTypeFiles = incomingFiles.filter(
      (file) =>
        !ACCEPTED_EXTENSIONS.has(getFileExtension(file.name))
    );

    if (invalidTypeFiles.length) {
      notify(
        `Unsupported file: ${invalidTypeFiles[0].name}`,
        "error"
      );

      event.target.value = "";
      return;
    }

    const oversizedFiles = incomingFiles.filter(
      (file) => file.size > MAX_FILE_SIZE
    );

    if (oversizedFiles.length) {
      notify(
        `${oversizedFiles[0].name} is larger than 20 MB`,
        "error"
      );

      event.target.value = "";
      return;
    }

    setFiles((previousFiles) => {
      const existingKeys = new Set(
        previousFiles.map(createFileKey)
      );

      const uniqueIncomingFiles = incomingFiles.filter(
        (file) => !existingKeys.has(createFileKey(file))
      );

      const combined = [
        ...previousFiles,
        ...uniqueIncomingFiles,
      ];

      if (combined.length > MAX_FILES) {
        notify(
          `You can select a maximum of ${MAX_FILES} documents`,
          "error"
        );

        return combined.slice(0, MAX_FILES);
      }

      return combined;
    });

    event.target.value = "";
  };

  const removeSelectedFile = (fileToRemove) => {
    const removeKey = createFileKey(fileToRemove);

    setFiles((previousFiles) =>
      previousFiles.filter(
        (file) => createFileKey(file) !== removeKey
      )
    );
  };

  const upload = async () => {
    if (!containerId) {
      notify("Container information is missing", "error");
      return;
    }

    if (!files.length) {
      notify("Please select at least one document", "error");
      return;
    }

    const formData = new FormData();

    files.forEach((file) => {
      formData.append("documents", file);
    });

    formData.append("documentType", documentType);
    formData.append("notes", notes);

    setUploading(true);

    try {
      await uploadContainerDocuments(containerId, formData);

      resetSelectedFiles();
      setNotes("");

      notify("Documents uploaded successfully");

      await load();
    } catch (error) {
      notify(
        getErrorMessage(error, "Document upload failed"),
        "error"
      );
    } finally {
      setUploading(false);
    }
  };

  const getDocumentBlob = async (document) => {
    const response = await api.get(
      getDocumentFileUrl(containerId, document.id),
      {
        responseType: "blob",
      }
    );

    const contentType =
      response.headers?.["content-type"] ||
      document.mimeType ||
      "application/octet-stream";

    return new Blob([response.data], {
      type: contentType,
    });
  };

  const viewDocument = async (document) => {
    const previewWindow = window.open("about:blank", "_blank");

    if (!previewWindow) {
      notify(
        "Please allow pop-ups to view this document",
        "error"
      );
      return;
    }

    previewWindow.opener = null;
    previewWindow.document.title = "Opening document...";
    previewWindow.document.body.innerHTML =
      '<p style="font-family:Arial;padding:24px">Opening document...</p>';

    setOpeningDocumentId(document.id);

    try {
      const blob = await getDocumentBlob(document);
      const objectUrl = URL.createObjectURL(blob);

      previewWindow.location.replace(objectUrl);

      window.setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
      }, 5 * 60 * 1000);
    } catch (error) {
      previewWindow.close();

      notify(
        getErrorMessage(error, "Unable to open document"),
        "error"
      );
    } finally {
      setOpeningDocumentId(null);
    }
  };

  const downloadDocument = async (document) => {
    setDownloadingDocumentId(document.id);

    try {
      const blob = await getDocumentBlob(document);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = window.document.createElement("a");

      anchor.href = objectUrl;
      anchor.download = document.originalName || "document";
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      window.setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
      }, 1000);
    } catch (error) {
      notify(
        getErrorMessage(error, "Unable to download document"),
        "error"
      );
    } finally {
      setDownloadingDocumentId(null);
    }
  };

  const canRemoveDocument = (document) => {
    if (canDelete) {
      return true;
    }

    return Boolean(
      canUpload &&
        currentUserId &&
        document.uploadedBy?.id !== undefined &&
        String(document.uploadedBy.id) === currentUserId
    );
  };

  const requestRemoveDocument = (document) => {
    if (deletingDocumentId) {
      return;
    }

    setDocumentToRemove(document);
  };

  const closeRemoveDocumentModal = useCallback(() => {
    if (deletingDocumentId) {
      return;
    }

    setDocumentToRemove(null);
  }, [deletingDocumentId]);

  const confirmRemoveDocument = async () => {
    const document = documentToRemove;

    if (!document || deletingDocumentId) {
      return;
    }

    setDeletingDocumentId(document.id);

    try {
      if (canDelete) {
        await deleteContainerDocument(
          containerId,
          document.id
        );
      } else {
        await api.delete(
          `/containers/${containerId}/documents/${document.id}/own`
        );
      }

      setDocumentToRemove(null);
      notify("Document removed successfully");

      await load();
    } catch (error) {
      notify(
        getErrorMessage(
          error,
          "Unable to remove document"
        ),
        "error"
      );
    } finally {
      setDeletingDocumentId(null);
    }
  };

  if (!canRead) {
    return null;
  }

  return (
    <>
      <RemoveDocumentModal
        document={documentToRemove}
        deleting={
          Boolean(documentToRemove) &&
          deletingDocumentId === documentToRemove.id
        }
        onCancel={closeRemoveDocumentModal}
        onConfirm={confirmRemoveDocument}
      />

      <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 dark:border-slate-800">
        <div>
          <h4 className="font-bold text-slate-950 dark:text-white">
            Container Documents
          </h4>

          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            View, download, or manage portal and email documents.
          </p>
        </div>

        {loading ? (
          <FiLoader className="animate-spin text-blue-600" />
        ) : null}
      </div>

      {canUpload ? (
        <div className="border-b border-slate-100 p-5 dark:border-slate-800">
          <div className="grid gap-3 md:grid-cols-2">
            <select
              value={documentType}
              onChange={(event) =>
                setDocumentType(event.target.value)
              }
              className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-blue-950"
            >
              {DOCUMENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-blue-300 bg-blue-50 px-3 py-2.5 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300 dark:hover:bg-blue-900">
              <FiUploadCloud />
              {selectedText}

              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.txt"
                onChange={handleFileSelection}
              />
            </label>
          </div>

          {files.length ? (
            <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-950">
              <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
                <div>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">
                    Selected documents
                  </p>

                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {files.length} file(s) ·{" "}
                    {formatBytes(selectedTotalSize)}
                  </p>
                </div>

                <button
                  type="button"
                  disabled={uploading}
                  onClick={resetSelectedFiles}
                  className="rounded-lg px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-950"
                >
                  Clear all
                </button>
              </div>

              <div className="max-h-44 divide-y divide-slate-200 overflow-y-auto dark:divide-slate-800">
                {files.map((file) => (
                  <div
                    key={createFileKey(file)}
                    className="flex items-center gap-3 px-4 py-3"
                  >
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                      <FiFile />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p
                        className="truncate text-sm font-semibold text-slate-900 dark:text-white"
                        title={file.name}
                      >
                        {file.name}
                      </p>

                      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        {formatBytes(file.size)}
                      </p>
                    </div>

                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() => removeSelectedFile(file)}
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 transition hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-950 dark:hover:text-red-300"
                      title={`Remove ${file.name}`}
                      aria-label={`Remove ${file.name}`}
                    >
                      <FiX />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <input
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Optional document note"
            className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-blue-950"
          />

          <button
            type="button"
            disabled={uploading || !files.length}
            onClick={upload}
            className="mt-3 w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {uploadButtonText}
          </button>
        </div>
      ) : null}

      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {!loading && documents.length === 0 ? (
          <p className="p-5 text-sm text-slate-500 dark:text-slate-400">
            No documents uploaded yet.
          </p>
        ) : null}

        {documents.map((document) => {
          const opening = openingDocumentId === document.id;
          const downloading =
            downloadingDocumentId === document.id;
          const deleting = deletingDocumentId === document.id;

          return (
            <div
              key={document.id}
              className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  {document.source === "email" ? (
                    <FiMail />
                  ) : (
                    <FiFile />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <p
                    className="truncate text-sm font-semibold text-slate-900 dark:text-white"
                    title={document.originalName}
                  >
                    {document.originalName}
                  </p>

                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {document.documentType}
                    {" • "}
                    {formatBytes(document.sizeBytes)}
                    {" • "}
                    {document.source}

                    {document.uploadedBy?.name
                      ? ` • ${document.uploadedBy.name}`
                      : ""}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                <button
                  type="button"
                  disabled={opening}
                  onClick={() => viewDocument(document)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:border-blue-900 dark:hover:bg-blue-950 dark:hover:text-blue-300"
                  title="View in new tab"
                >
                  {opening ? (
                    <FiLoader className="animate-spin" />
                  ) : (
                    <FiEye />
                  )}
                  View
                </button>

                <button
                  type="button"
                  disabled={downloading}
                  onClick={() => downloadDocument(document)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:border-blue-900 dark:hover:bg-blue-950 dark:hover:text-blue-300"
                  title="Download document"
                >
                  {downloading ? (
                    <FiLoader className="animate-spin" />
                  ) : (
                    <FiDownload />
                  )}
                  Download
                </button>

                {canRemoveDocument(document) ? (
                  <button
                    type="button"
                    disabled={deleting}
                    onClick={() =>
                      requestRemoveDocument(document)
                    }
                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950"
                    title="Remove document"
                  >
                    {deleting ? (
                      <FiLoader className="animate-spin" />
                    ) : (
                      <FiTrash2 />
                    )}
                    Remove
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      </section>
    </>
  );
}